import React from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const handleButtonClick = (packName: string) => {
    if (!user) {
      // Redirect to login page with return URL
      navigate('/login', { state: { from: packName === 'Basic' ? '/' : '/buy-credits' } });
    } else {
      // User is authenticated
      if (packName === 'Basic') {
        // Navigate to dashboard for free tier
        navigate('/dashboard');
      } else {
        // Navigate to buy credits page for paid tiers
        navigate('/buy-credits', { state: { selectedPack: packName.toLowerCase() } });
      }
    }
  };
  const creditPacks = [
    {
      name: 'Basic',
      price: '0',
      credits: '10,000',
      features: [
        'Standard TTS voices',
        '10,000 characters total',
        '500 characters per request',
        'Basic voice selection',
        'MP3 downloads',
        'Community support',
      ],
      cta: 'Start Free',
      popular: false,
      icon: '🔊',
      gradient: 'from-blue-400 to-indigo-600',
      cardGradient: 'from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-900',
      buttonGradient: 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
      accentColor: 'text-blue-600 dark:text-blue-400',
      borderGradient: 'border-blue-200 dark:border-blue-900',
      iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      name: 'Standard',
      price: '5',
      credits: '100,000',
      features: [
        'High-quality Premium voices',
        '100,000 characters total',
        '5,000 characters per request',
        'Priority processing',
        'Advanced audio settings',
        'Email support',
        'Commercial usage rights',
        'No expiration date',
      ],
      cta: 'Buy Credits',
      popular: true,
      icon: '⭐',
      gradient: 'from-violet-500 to-purple-600',
      cardGradient: 'from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20',
      buttonGradient: 'from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700',
      accentColor: 'text-violet-600 dark:text-violet-400',
      borderGradient: 'border-violet-200 dark:border-violet-800',
      iconBg: 'bg-violet-100 dark:bg-violet-900/30',
    },
    {
      name: 'Premium',
      price: '10',
      credits: '250,000',
      features: [
        'High-quality Premium voices',
        '250,000 characters total',
        '10,000 characters per request',
        'Highest priority processing',
        'Advanced audio settings',
        '24/7 priority support',
        'SLA guarantees',
        'Advanced analytics',
        'No expiration date',
      ],
      cta: 'Buy Credits',
      popular: false,
      icon: '👑',
      gradient: 'from-amber-400 to-orange-600',
      cardGradient: 'from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20',
      buttonGradient: 'from-amber-400 to-orange-600 hover:from-amber-500 hover:to-orange-700',
      accentColor: 'text-amber-600 dark:text-amber-400',
      borderGradient: 'border-amber-200 dark:border-amber-800',
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    },
  ];

  return (
    <section id="pricing" className="py-20 bg-gray-50 dark:bg-dark-900 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 animate-gradient bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500 bg-[length:200%_auto] bg-clip-text text-transparent">
            Text to Speech Pricing - Free AI Voice Generator Plans
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-blue-500 mx-auto my-6 rounded-full"></div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Start free with 10,000 characters. Purchase credits once and use them whenever you need. No subscriptions, no expiration.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {creditPacks.map((pack) => (
            <div
              key={pack.name}
              className={`relative bg-gradient-to-br ${pack.cardGradient} rounded-2xl shadow-xl overflow-hidden transition-all duration-300 border ${pack.borderGradient} hover:shadow-2xl hover:scale-[1.02] ${
                pack.popular 
                  ? 'ring-2 ring-violet-500 dark:ring-violet-400 transform scale-[1.02]' 
                  : 'dark:shadow-purple-500/5'
              }`}
            >
              {pack.popular && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-violet-500 to-purple-600 text-white px-4 py-1 text-sm font-medium rounded-bl-lg">
                  Best Value
                </div>
              )}
              <div className="p-8">
                <div className={`${pack.iconBg} w-14 h-14 rounded-full flex items-center justify-center mb-6`}>
                  <span className="text-2xl">{pack.icon}</span>
                </div>
                <h3 className={`text-2xl font-bold ${pack.accentColor} mb-2`}>
                  {pack.name}
                </h3>
                <div className="flex items-baseline mb-6">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white">
                    ${pack.price}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2 text-sm">
                    for {pack.credits} credits
                  </span>
                </div>
                <ul className="space-y-4 mb-8">
                  {pack.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className={`w-5 h-5 ${pack.accentColor} mr-2 flex-shrink-0`} />
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleButtonClick(pack.name)}
                  className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-300 bg-gradient-to-r ${pack.buttonGradient} text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5`}
                >
                  {pack.cta}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;